import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Autocomplete,
  Grid,
  Chip,
  ButtonGroup,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import TableChartIcon from '@mui/icons-material/TableChart';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { projectService } from '../../services/projectService';
import { sowService } from '../../services/sowService';
import XLSX from 'xlsx-js-style';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const SOWReport = () => {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [startMonth, setStartMonth] = useState('');
  const [endMonth, setEndMonth] = useState('');
  const [reportData, setReportData] = useState(null);
  const [months, setMonths] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const now = new Date();
    const sixMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 5, 1);
    setStartMonth(formatMonthInput(sixMonthsAgo));
    setEndMonth(formatMonthInput(now));
  }, []);

  useEffect(() => {
    loadProjects();
  }, []);

  const formatMonthInput = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return year + '-' + month;
  };

  const loadProjects = async () => {
    try {
      const data = await projectService.getAll();
      setProjects(data || []);
    } catch (err) {
      console.error('Failed to load projects:', err);
      setError('Failed to load projects');
    }
  };

  const handleLoadReport = async () => {
    if (!startMonth || !endMonth) {
      setError('Please select start and end months');
      return;
    }
    const startDate = new Date(startMonth + '-01');
    const endDate = new Date(endMonth + '-01');
    if (startDate > endDate) {
      setError('Start month must be before or equal to end month');
      return;
    }
    try {
      setLoading(true);
      setError('');
      const projectCode = selectedProject?.project_code || 'ALL';
      const response = await sowService.getMonthlyBurnReport(projectCode, startMonth + '-01', endMonth + '-01');
      setReportData(response.report || []);
      setMonths(response.months || []);
    } catch (err) {
      console.error('Failed to load report:', err);
      setError(err.response?.data?.error || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  const formatMonthDisplay = (monthStr) => {
    const date = new Date(monthStr);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
  };

  const formatCurrency = (value) => {
    if (value === 0) return '-';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
  };

  const formatCurrencyNumber = (value) => {
    return value || 0;
  };

  const calculateGrandTotals = () => {
    if (!reportData || reportData.length === 0) return null;
    const totals = { total_sow_value: 0, total_burn_value: 0, remaining_value: 0, monthly_burns: {}, total_range_burn: 0 };
    months.forEach(month => { totals.monthly_burns[month] = 0; });
    reportData.forEach(row => {
      totals.total_sow_value += row.total_sow_value || 0;
      totals.total_burn_value += row.total_burn_value || 0;
      totals.remaining_value += row.remaining_value || 0;
      totals.total_range_burn += row.total_range_burn || 0;
      months.forEach(month => { totals.monthly_burns[month] += row.monthly_burns?.[month] || 0; });
    });
    return totals;
  };

  const grandTotals = calculateGrandTotals();
  const projectOptions = [{ project_code: 'ALL', name: 'All Projects' }, ...projects];

  // Export to Excel with styling
  const handleExportExcel = () => {
    if (!reportData || reportData.length === 0) return;

    const headers = ['SNo', 'Project Code', 'Project Name', 'Total SOW', 'Burn Value', 'Remaining'];
    months.forEach(month => headers.push(formatMonthDisplay(month)));
    headers.push('Total (Range)');

    // Create styled header row
    const headerRow = headers.map((header, index) => {
      let bgColor = '667EEA'; // Default purple
      if (index === 3) bgColor = '2E7D32'; // Dark green for Total SOW
      else if (index === 4) bgColor = 'E65100'; // Dark orange for Burn Value
      else if (index === 5) bgColor = '7B1FA2'; // Dark purple for Remaining
      else if (index > 5 && index < headers.length - 1) bgColor = index % 2 === 0 ? '1976D2' : '1565C0'; // Dark blue for months
      else if (index === headers.length - 1) bgColor = '283593'; // Dark indigo for Total Range

      return {
        v: header,
        t: 's',
        s: {
          font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 11 },
          fill: { fgColor: { rgb: bgColor } },
          alignment: { horizontal: 'center', vertical: 'center' },
          border: {
            top: { style: 'thin', color: { rgb: 'FFFFFF' } },
            bottom: { style: 'thin', color: { rgb: 'FFFFFF' } },
            left: { style: 'thin', color: { rgb: 'FFFFFF' } },
            right: { style: 'thin', color: { rgb: 'FFFFFF' } }
          }
        }
      };
    });

    // Create data rows with alternating colors
    const dataRows = reportData.map((row, rowIndex) => {
      const bgColor = rowIndex % 2 === 0 ? 'E3F2FD' : 'FFFFFF'; // Light blue and white
      const rowData = [
        rowIndex + 1,
        row.project_code,
        row.project_name,
        formatCurrencyNumber(row.total_sow_value),
        formatCurrencyNumber(row.total_burn_value),
        formatCurrencyNumber(row.remaining_value),
      ];
      months.forEach(month => rowData.push(formatCurrencyNumber(row.monthly_burns?.[month])));
      rowData.push(formatCurrencyNumber(row.total_range_burn));

      return rowData.map((cell, colIndex) => {
        const isNumber = colIndex >= 3;
        const cellBgColor = colIndex === rowData.length - 1 ? (rowIndex % 2 === 0 ? 'C5CAE9' : 'E8EAF6') : bgColor;
        let textColor = '000000';
        if (colIndex === 1) textColor = '5C6BC0';
        else if (colIndex === 3) textColor = '2E7D32';
        else if (colIndex === 4) textColor = 'E65100';
        else if (colIndex === 5) textColor = '7B1FA2';
        else if (colIndex > 5 && colIndex < rowData.length - 1) textColor = '1565C0';
        else if (colIndex === rowData.length - 1) textColor = '3F51B5';

        return {
          v: cell,
          t: isNumber ? 'n' : 's',
          s: {
            font: {
              color: { rgb: textColor },
              bold: colIndex === 1 || colIndex === rowData.length - 1,
              sz: 10
            },
            fill: { fgColor: { rgb: cellBgColor } },
            alignment: { horizontal: isNumber ? 'right' : 'left', vertical: 'center' },
            numFmt: isNumber ? '$#,##0.00' : undefined,
            border: {
              top: { style: 'thin', color: { rgb: 'E0E0E0' } },
              bottom: { style: 'thin', color: { rgb: 'E0E0E0' } },
              left: { style: 'thin', color: { rgb: 'E0E0E0' } },
              right: { style: 'thin', color: { rgb: 'E0E0E0' } }
            }
          }
        };
      });
    });

    // Create grand totals row
    let totalsRow = [];
    if (grandTotals) {
      const totalsData = [
        '',
        'GRAND TOTAL',
        '',
        formatCurrencyNumber(grandTotals.total_sow_value),
        formatCurrencyNumber(grandTotals.total_burn_value),
        formatCurrencyNumber(grandTotals.remaining_value),
      ];
      months.forEach(month => totalsData.push(formatCurrencyNumber(grandTotals.monthly_burns[month])));
      totalsData.push(formatCurrencyNumber(grandTotals.total_range_burn));

      totalsRow = totalsData.map((cell, colIndex) => {
        const isNumber = colIndex >= 3;
        return {
          v: cell,
          t: isNumber ? 'n' : 's',
          s: {
            font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 11 },
            fill: { fgColor: { rgb: colIndex === totalsData.length - 1 ? '3F51B5' : '764BA2' } },
            alignment: { horizontal: isNumber ? 'right' : 'left', vertical: 'center' },
            numFmt: isNumber ? '$#,##0.00' : undefined,
            border: {
              top: { style: 'thin', color: { rgb: 'FFFFFF' } },
              bottom: { style: 'thin', color: { rgb: 'FFFFFF' } },
              left: { style: 'thin', color: { rgb: 'FFFFFF' } },
              right: { style: 'thin', color: { rgb: 'FFFFFF' } }
            }
          }
        };
      });
    }

    // Build worksheet
    const wsData = [headerRow, ...dataRows];
    if (totalsRow.length > 0) wsData.push(totalsRow);

    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // Set column widths
    const colWidths = [
      { wch: 6 },  // SNo
      { wch: 14 }, // Project Code
      { wch: 25 }, // Project Name
      { wch: 14 }, // Total SOW
      { wch: 14 }, // Burn Value
      { wch: 14 }, // Remaining
    ];
    months.forEach(() => colWidths.push({ wch: 12 }));
    colWidths.push({ wch: 14 }); // Total Range
    ws['!cols'] = colWidths;

    // Set row heights
    ws['!rows'] = [{ hpt: 25 }]; // Header row height

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'SOW Report');

    const fileName = 'SOW_Report_' + startMonth + '_to_' + endMonth + '.xlsx';
    XLSX.writeFile(wb, fileName);
  };

  // Export to PDF
  const handleExportPDF = () => {
    if (!reportData || reportData.length === 0) return;

    try {
      const doc = new jsPDF('landscape', 'mm', 'a4');

      // Title
      doc.setFontSize(18);
      doc.setTextColor(102, 126, 234);
      doc.text('SOW Report', 14, 15);

      // Subtitle
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text('Period: ' + formatMonthDisplay(startMonth + '-01') + ' to ' + formatMonthDisplay(endMonth + '-01'), 14, 22);

      const headers = [['SNo', 'Project Code', 'Project Name', 'Total SOW', 'Burn Value', 'Remaining']];
      months.forEach(month => headers[0].push(formatMonthDisplay(month)));
      headers[0].push('Total (Range)');

      const data = reportData.map((row, index) => {
        const rowData = [
          index + 1,
          row.project_code,
          row.project_name,
          formatCurrency(row.total_sow_value),
          formatCurrency(row.total_burn_value),
          formatCurrency(row.remaining_value),
        ];
        months.forEach(month => rowData.push(formatCurrency(row.monthly_burns?.[month] || 0)));
        rowData.push(formatCurrency(row.total_range_burn));
        return rowData;
      });

      // Add grand totals row
      if (grandTotals) {
        const totalsRow = [
          '',
          'GRAND TOTAL',
          '',
          formatCurrency(grandTotals.total_sow_value),
          formatCurrency(grandTotals.total_burn_value),
          formatCurrency(grandTotals.remaining_value),
        ];
        months.forEach(month => totalsRow.push(formatCurrency(grandTotals.monthly_burns[month])));
        totalsRow.push(formatCurrency(grandTotals.total_range_burn));
        data.push(totalsRow);
      }

      const totalRows = data.length;

      autoTable(doc, {
        head: headers,
        body: data,
        startY: 28,
        styles: {
          fontSize: 7,
          cellPadding: 2,
          lineColor: [200, 200, 200],
          lineWidth: 0.1,
        },
        headStyles: {
          fillColor: [102, 126, 234],
          textColor: [255, 255, 255],
          fontStyle: 'bold',
          halign: 'center',
        },
        columnStyles: {
          0: { halign: 'center', cellWidth: 10 },
          1: { fontStyle: 'bold', textColor: [92, 107, 192] },
          3: { halign: 'right', textColor: [46, 125, 50] },
          4: { halign: 'right', textColor: [230, 81, 0] },
          5: { halign: 'right', textColor: [123, 31, 162] },
        },
        didParseCell: function(hookData) {
          const colCount = headers[0].length;
          // Monthly columns
          if (hookData.column.index > 5 && hookData.column.index < colCount - 1) {
            hookData.cell.styles.halign = 'right';
            if (hookData.section === 'body') {
              hookData.cell.styles.textColor = [21, 101, 192];
            }
          }
          // Total Range column
          if (hookData.column.index === colCount - 1) {
            hookData.cell.styles.halign = 'right';
            hookData.cell.styles.fontStyle = 'bold';
            if (hookData.section === 'head') {
              hookData.cell.styles.fillColor = [63, 81, 181];
            }
            if (hookData.section === 'body' && hookData.row.index < totalRows - 1) {
              hookData.cell.styles.textColor = [63, 81, 181];
            }
          }
          // Alternate row colors
          if (hookData.section === 'body' && hookData.row.index < totalRows - 1) {
            if (hookData.row.index % 2 === 0) {
              hookData.cell.styles.fillColor = [227, 242, 253]; // Light blue
            } else {
              hookData.cell.styles.fillColor = [255, 255, 255]; // White
            }
          }
          // Grand totals row styling
          if (hookData.section === 'body' && hookData.row.index === totalRows - 1) {
            hookData.cell.styles.fillColor = [118, 75, 162];
            hookData.cell.styles.textColor = [255, 255, 255];
            hookData.cell.styles.fontStyle = 'bold';
            if (hookData.column.index === colCount - 1) {
              hookData.cell.styles.fillColor = [63, 81, 181];
            }
          }
        },
      });

      const fileName = 'SOW_Report_' + startMonth + '_to_' + endMonth + '.pdf';
      doc.save(fileName);
    } catch (err) {
      console.error('PDF export error:', err);
      setError('Failed to export PDF: ' + err.message);
    }
  };

  // Alternate row colors - more visible contrast
  const evenRowColor = '#E3F2FD'; // Light blue
  const oddRowColor = '#FFFFFF';  // White

  return (
    <Container maxWidth="xl">
      <Box sx={{ mt: 3, mb: 4 }}>
        {/* Header Section */}
        <Box sx={{
          mb: 2,
          p: 1.5,
          px: 2,
          borderRadius: 2,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          boxShadow: '0 2px 10px rgba(102, 126, 234, 0.3)',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <TrendingUpIcon sx={{ fontSize: 28 }} />
            <Box>
              <Typography variant="h6" fontWeight="bold">SOW Report</Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                View SOW values and monthly burn breakdown
              </Typography>
            </Box>
          </Box>
        </Box>

        {error && (<Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>)}

        {/* Filter Section */}
        <Paper sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
          border: '1px solid #E8EAF6',
        }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <Autocomplete
                options={projectOptions}
                getOptionLabel={(option) => option.project_code === 'ALL' ? 'All Projects' : option.name + ' (' + option.project_code + ')'}
                value={selectedProject || projectOptions[0]}
                onChange={(event, newValue) => { setSelectedProject(newValue?.project_code === 'ALL' ? null : newValue); }}
                isOptionEqualToValue={(option, value) => option.project_code === value?.project_code}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Project"
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      }
                    }}
                  />
                )}
                fullWidth
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                fullWidth
                label="Start Month"
                type="month"
                value={startMonth}
                onChange={(e) => setStartMonth(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                fullWidth
                label="End Month"
                type="month"
                value={endMonth}
                onChange={(e) => setEndMonth(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="contained"
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
                onClick={handleLoadReport}
                disabled={loading}
                sx={{
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  boxShadow: '0 4px 14px rgba(102, 126, 234, 0.4)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5a6fd6 0%, #6a4190 100%)',
                  }
                }}
              >
                {loading ? 'Loading...' : 'Load Report'}
              </Button>
            </Grid>
            <Grid item xs={12} md={2}>
              {reportData && reportData.length > 0 && (
                <ButtonGroup variant="outlined" fullWidth sx={{ borderRadius: 2 }}>
                  <Button
                    startIcon={<TableChartIcon />}
                    onClick={handleExportExcel}
                    sx={{
                      borderRadius: '8px 0 0 8px',
                      color: '#2E7D32',
                      borderColor: '#2E7D32',
                      '&:hover': {
                        backgroundColor: '#E8F5E9',
                        borderColor: '#2E7D32',
                      }
                    }}
                  >
                    Excel
                  </Button>
                  <Button
                    startIcon={<PictureAsPdfIcon />}
                    onClick={handleExportPDF}
                    sx={{
                      borderRadius: '0 8px 8px 0',
                      color: '#C62828',
                      borderColor: '#C62828',
                      '&:hover': {
                        backgroundColor: '#FFEBEE',
                        borderColor: '#C62828',
                      }
                    }}
                  >
                    PDF
                  </Button>
                </ButtonGroup>
              )}
            </Grid>
          </Grid>
        </Paper>

        {/* Data Table */}
        {reportData && reportData.length > 0 && (
          <Paper sx={{
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          }}>
            {/* Summary Chips */}
            <Box sx={{
              p: 2,
              background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8f0 100%)',
              borderBottom: '1px solid #E0E0E0',
              display: 'flex',
              gap: 2,
              flexWrap: 'wrap',
            }}>
              <Chip
                label={'Projects: ' + reportData.length}
                sx={{
                  backgroundColor: '#E3F2FD',
                  color: '#1565C0',
                  fontWeight: 600,
                }}
              />
              {grandTotals && (
                <>
                  <Chip
                    label={'Total SOW: ' + formatCurrency(grandTotals.total_sow_value)}
                    sx={{
                      backgroundColor: '#E8F5E9',
                      color: '#2E7D32',
                      fontWeight: 600,
                    }}
                  />
                  <Chip
                    label={'Total Burn: ' + formatCurrency(grandTotals.total_burn_value)}
                    sx={{
                      backgroundColor: '#FFF3E0',
                      color: '#E65100',
                      fontWeight: 600,
                    }}
                  />
                  <Chip
                    label={'Remaining: ' + formatCurrency(grandTotals.remaining_value)}
                    sx={{
                      backgroundColor: '#F3E5F5',
                      color: '#7B1FA2',
                      fontWeight: 600,
                    }}
                  />
                </>
              )}
            </Box>

            <TableContainer sx={{ maxHeight: 'calc(100vh - 420px)', overflow: 'auto' }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      fontWeight: 'bold',
                      position: 'sticky',
                      left: 0,
                      zIndex: 3,
                      minWidth: 50,
                      borderBottom: 'none',
                    }} align="center">SNo</TableCell>
                    <TableCell sx={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      fontWeight: 'bold',
                      position: 'sticky',
                      left: 50,
                      zIndex: 3,
                      minWidth: 100,
                      borderBottom: 'none',
                    }}>Project Code</TableCell>
                    <TableCell sx={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      fontWeight: 'bold',
                      position: 'sticky',
                      left: 150,
                      zIndex: 3,
                      minWidth: 180,
                      borderBottom: 'none',
                    }}>Project Name</TableCell>
                    <TableCell sx={{
                      backgroundColor: '#2E7D32',
                      color: 'white',
                      fontWeight: 'bold',
                      minWidth: 120,
                      borderBottom: '3px solid #1B5E20',
                      textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
                      fontSize: '0.85rem',
                    }} align="right">Total SOW</TableCell>
                    <TableCell sx={{
                      backgroundColor: '#E65100',
                      color: 'white',
                      fontWeight: 'bold',
                      minWidth: 120,
                      borderBottom: '3px solid #BF360C',
                      textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
                      fontSize: '0.85rem',
                    }} align="right">Burn Value</TableCell>
                    <TableCell sx={{
                      backgroundColor: '#7B1FA2',
                      color: 'white',
                      fontWeight: 'bold',
                      minWidth: 120,
                      borderBottom: '3px solid #4A148C',
                      textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
                      fontSize: '0.85rem',
                    }} align="right">Remaining</TableCell>
                    {months.map((month, index) => (
                      <TableCell
                        key={month}
                        sx={{
                          backgroundColor: index % 2 === 0 ? '#1976D2' : '#1565C0',
                          color: 'white',
                          fontWeight: 'bold',
                          minWidth: 100,
                          borderBottom: '3px solid #0D47A1',
                          textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
                          fontSize: '0.8rem',
                        }}
                        align="right"
                      >
                        {formatMonthDisplay(month)}
                      </TableCell>
                    ))}
                    <TableCell sx={{
                      backgroundColor: '#283593',
                      color: 'white',
                      fontWeight: 'bold',
                      minWidth: 120,
                      borderBottom: '3px solid #1A237E',
                      textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
                      fontSize: '0.85rem',
                    }} align="right">Total (Range)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reportData.map((row, rowIndex) => (
                    <TableRow
                      key={row.project_code}
                      sx={{
                        backgroundColor: rowIndex % 2 === 0 ? evenRowColor : oddRowColor,
                        '&:hover': {
                          backgroundColor: '#BBDEFB !important',
                          '& td': { backgroundColor: '#BBDEFB !important' }
                        },
                        transition: 'background-color 0.2s',
                      }}
                    >
                      <TableCell align="center" sx={{
                        position: 'sticky',
                        left: 0,
                        backgroundColor: rowIndex % 2 === 0 ? evenRowColor : oddRowColor,
                        zIndex: 1,
                        borderBottom: '1px solid #BBDEFB',
                      }}>{rowIndex + 1}</TableCell>
                      <TableCell sx={{
                        position: 'sticky',
                        left: 50,
                        backgroundColor: rowIndex % 2 === 0 ? evenRowColor : oddRowColor,
                        zIndex: 1,
                        fontWeight: 'bold',
                        color: '#5C6BC0',
                        borderBottom: '1px solid #BBDEFB',
                      }}>{row.project_code}</TableCell>
                      <TableCell sx={{
                        position: 'sticky',
                        left: 150,
                        backgroundColor: rowIndex % 2 === 0 ? evenRowColor : oddRowColor,
                        zIndex: 1,
                        borderBottom: '1px solid #BBDEFB',
                        fontWeight: 500,
                      }}>{row.project_name}</TableCell>
                      <TableCell align="right" sx={{
                        color: '#2E7D32',
                        fontWeight: 600,
                        borderBottom: '1px solid #BBDEFB',
                        backgroundColor: rowIndex % 2 === 0 ? '#E8F5E9' : '#F1F8E9',
                      }}>{formatCurrency(row.total_sow_value)}</TableCell>
                      <TableCell align="right" sx={{
                        color: '#E65100',
                        fontWeight: 600,
                        borderBottom: '1px solid #BBDEFB',
                        backgroundColor: rowIndex % 2 === 0 ? '#FFF3E0' : '#FFF8E1',
                      }}>{formatCurrency(row.total_burn_value)}</TableCell>
                      <TableCell align="right" sx={{
                        color: '#7B1FA2',
                        fontWeight: 600,
                        borderBottom: '1px solid #BBDEFB',
                        backgroundColor: rowIndex % 2 === 0 ? '#F3E5F5' : '#FCE4EC',
                      }}>{formatCurrency(row.remaining_value)}</TableCell>
                      {months.map((month, monthIndex) => (
                        <TableCell
                          key={month}
                          align="right"
                          sx={{
                            color: '#1565C0',
                            fontWeight: 500,
                            borderBottom: '1px solid #BBDEFB',
                            backgroundColor: rowIndex % 2 === 0
                              ? (monthIndex % 2 === 0 ? '#E3F2FD' : '#E1F5FE')
                              : (monthIndex % 2 === 0 ? '#F5F5F5' : '#FAFAFA'),
                          }}
                        >
                          {formatCurrency(row.monthly_burns?.[month] || 0)}
                        </TableCell>
                      ))}
                      <TableCell align="right" sx={{
                        fontWeight: 'bold',
                        backgroundColor: rowIndex % 2 === 0 ? '#C5CAE9' : '#E8EAF6',
                        color: '#3F51B5',
                        borderBottom: '1px solid #BBDEFB',
                      }}>{formatCurrency(row.total_range_burn)}</TableCell>
                    </TableRow>
                  ))}
                  {grandTotals && (
                    <TableRow sx={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    }}>
                      <TableCell
                        colSpan={3}
                        sx={{
                          fontWeight: 'bold',
                          fontSize: '1rem',
                          position: 'sticky',
                          left: 0,
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          color: 'white',
                          zIndex: 1,
                          borderBottom: 'none',
                        }}
                      >
                        GRAND TOTAL
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', color: 'white', borderBottom: 'none', backgroundColor: '#43A047' }}>
                        {formatCurrency(grandTotals.total_sow_value)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', color: 'white', borderBottom: 'none', backgroundColor: '#FB8C00' }}>
                        {formatCurrency(grandTotals.total_burn_value)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', color: 'white', borderBottom: 'none', backgroundColor: '#8E24AA' }}>
                        {formatCurrency(grandTotals.remaining_value)}
                      </TableCell>
                      {months.map((month) => (
                        <TableCell key={month} align="right" sx={{ fontWeight: 'bold', color: 'white', borderBottom: 'none' }}>
                          {formatCurrency(grandTotals.monthly_burns[month])}
                        </TableCell>
                      ))}
                      <TableCell align="right" sx={{
                        fontWeight: 'bold',
                        backgroundColor: '#3F51B5',
                        color: 'white',
                        borderBottom: 'none',
                      }}>
                        {formatCurrency(grandTotals.total_range_burn)}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        {reportData && reportData.length === 0 && (
          <Paper sx={{
            p: 6,
            textAlign: 'center',
            borderRadius: 3,
            background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8f0 100%)',
          }}>
            <Typography color="textSecondary" variant="h6">No data found for the selected criteria</Typography>
          </Paper>
        )}

        {!reportData && !loading && (
          <Paper sx={{
            p: 6,
            textAlign: 'center',
            borderRadius: 3,
            background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8f0 100%)',
          }}>
            <DownloadIcon sx={{ fontSize: 60, color: '#9E9E9E', mb: 2 }} />
            <Typography color="textSecondary" variant="h6">
              Select filters and click Load Report to view SOW data
            </Typography>
          </Paper>
        )}
      </Box>
    </Container>
  );
};

export default SOWReport;
